<script setup lang="ts">
import {
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandSeparator,
} from "@/components/ui/command";
import { ref, onMounted, onUnmounted, watch, computed } from "vue";
import {
  Globe,
  CornerDownLeft,
  ChevronDown,
  ChevronRight,
  TrendingUp,
} from "lucide-vue-next";
import { useRouter } from "vue-router";
import regions from "@/data/regions.json";
import popularRegions from "@/data/popularRegions.json";

import type { Region, SubLocation, PopularCountry } from "@/lib/types";

const router = useRouter();

interface Props {
  open: boolean;
  disableKeyboardShortcut?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  disableKeyboardShortcut: false,
});

const emit = defineEmits<{
  "update:open": [value: boolean];
}>();

const expandedRegions = ref(new Set<string>());

const allRegions = regions as Region[];

const popularRegionsList = computed((): PopularCountry[] => {
  const { countries } = popularRegions;

  return countries.slice(0, 6);
});

const toggleRegion = (code: string, event: Event) => {
  event.preventDefault();
  event.stopPropagation();

  const expanded = expandedRegions.value;
  if (expanded.has(code)) {
    expanded.delete(code);
  } else {
    expanded.add(code);
  }
  expandedRegions.value = new Set(expanded);
};

const handleKeyDown = (e: KeyboardEvent) => {
  const target = e.target;

  if (
    target instanceof HTMLInputElement ||
    target instanceof HTMLTextAreaElement ||
    target instanceof HTMLSelectElement ||
    (target instanceof HTMLElement && target.isContentEditable)
  ) {
    return;
  }

  if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
    e.preventDefault();
    emit("update:open", !props.open);
  }
};

const handleSelectRegion = (
  region: Region | SubLocation | PopularCountry,
  multiCountryCode?: string
) => {
  emit("update:open", false);
  expandedRegions.value.clear();
  router.push(`/regions/${multiCountryCode || region.code}`);
};

const handleSearchInput = () => {
  expandedRegions.value = new Set();
};

watch(
  () => props.open,
  (isOpen) => {
    if (!isOpen) {
      expandedRegions.value = new Set();
    }
  }
);

onMounted(() => {
  if (!props.disableKeyboardShortcut) {
    document.addEventListener("keydown", handleKeyDown);
  }
});

onUnmounted(() => {
  if (!props.disableKeyboardShortcut) {
    document.removeEventListener("keydown", handleKeyDown);
  }
});
</script>

<template>
  <CommandDialog :open="open" @update:open="(val) => emit('update:open', val)">
    <CommandInput
      placeholder="Type to search regions..."
      @update:model-value="handleSearchInput"
    />

    <CommandList class="min-h-80 max-h-[400px]">
      <CommandEmpty>No results found.</CommandEmpty>

      <CommandGroup heading="Popular">
        <CommandItem
          v-for="region in popularRegionsList"
          :key="region.code"
          :value="`${region.code} ${region.name}`"
          @select="handleSelectRegion(region)"
          class="flex items-center gap-2"
        >
          <TrendingUp :size="16" class="shrink-0 text-blue-500 ml-6" />
          <span class="flex-1">{{ region.name }}</span>
          <span class="text-xs text-muted-foreground shrink-0">{{
            region.code
          }}</span>
        </CommandItem>
      </CommandGroup>

      <CommandSeparator />

      <CommandGroup heading="Regions">
        <template v-for="region in allRegions" :key="region.code">
          <CommandItem
            :value="`${region.code} ${region.name}`"
            @select="handleSelectRegion(region)"
            class="flex items-center gap-2"
          >
            <button
              v-if="region.subLocationList?.length"
              @click="(e) => toggleRegion(region.code, e)"
              class="p-0.5 hover:bg-accent rounded-sm transition-colors"
              type="button"
              :aria-label="
                expandedRegions.has(region.code) ? 'Collapse' : 'Expand'
              "
            >
              <component
                :is="
                  expandedRegions.has(region.code) ? ChevronDown : ChevronRight
                "
                :size="14"
                class="text-muted-foreground"
              />
            </button>
            <div v-else class="w-[18px]" aria-hidden="true" />

            <Globe :size="16" class="shrink-0" />
            <span class="flex-1">{{ region.name }}</span>
            <span class="text-xs text-muted-foreground shrink-0">{{
              region.code
            }}</span>
          </CommandItem>

          <div
            v-if="
              region.subLocationList?.length && expandedRegions.has(region.code)
            "
            class="ml-8 mt-1 mb-2 space-y-1 border-l-2 border-border pl-3"
          >
            <div
              v-for="subRegion in region.subLocationList"
              :key="subRegion.code"
              @click="handleSelectRegion(subRegion as Region, region.code)"
              class="flex items-center gap-2 py-1.5 px-2 text-sm text-muted-foreground rounded-md cursor-pointer hover:bg-accent transition-colors"
              role="button"
              tabindex="0"
              @keydown.enter="
                handleSelectRegion(subRegion as Region, region.code)
              "
            >
              <Globe :size="14" class="shrink-0 opacity-60" />
              <span class="flex-1 text-xs">{{ subRegion.name }}</span>
              <span class="text-xs opacity-60 shrink-0">{{
                subRegion.code
              }}</span>
            </div>
          </div>
        </template>
      </CommandGroup>
    </CommandList>

    <div class="flex h-10" aria-hidden="true" />
    <div
      class="absolute inset-x-0 bottom-0 flex h-10 items-center justify-between gap-2 border-t bg-zinc-100/30 px-4 text-xs font-medium dark:bg-zinc-800/30"
    >
      <div class="flex items-center gap-2 text-muted-foreground">Jittr</div>

      <div class="flex shrink-0 items-center gap-2">
        <span>Select</span>
        <kbd
          class="pointer-events-none flex h-5 min-w-6 items-center justify-center gap-1 rounded-sm bg-black/5 px-1 font-sans text-[13px] font-normal text-muted-foreground shadow-[inset_0_-1px_2px] shadow-black/10 select-none dark:bg-white/10 dark:shadow-white/10"
        >
          <CornerDownLeft :size="12" />
        </kbd>
        <span class="text-muted-foreground ml-2">•</span>
        <span class="text-muted-foreground">Exit</span>
        <kbd
          class="pointer-events-none flex h-5 min-w-6 items-center justify-center gap-1 rounded-sm bg-black/5 px-1 font-sans text-[13px] font-normal text-muted-foreground shadow-[inset_0_-1px_2px] shadow-black/10 select-none dark:bg-white/10 dark:shadow-white/10"
        >
          Esc
        </kbd>
      </div>
    </div>
  </CommandDialog>
</template>
